import axios from "axios";

type SseEventHandler = (data: unknown) => void;

const INITIAL_RECONNECT_DELAY_MS = 3000;
const MAX_RECONNECT_DELAY_MS = 30000;

function encodeTokenForSse(token: string): string {
  return btoa(token);
}

export class FrontendSseClient {
  private eventSource: EventSource | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private listeners = new Map<string, Set<SseEventHandler>>();
  private getToken: (() => string | undefined) | null = null;
  private intentionalClose = false;
  private reconnectDelayMs = INITIAL_RECONNECT_DELAY_MS;

  subscribe(event: string, handler: SseEventHandler): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }

    this.listeners.get(event)!.add(handler);

    return () => {
      this.listeners.get(event)?.delete(handler);
    };
  }

  connect(getToken: () => string | undefined): void {
    this.getToken = getToken;
    this.intentionalClose = false;
    this.openConnection();
  }

  disconnect(): void {
    this.intentionalClose = true;
    this.clearReconnectTimeout();
    this.closeEventSource();
    this.getToken = null;
    this.reconnectDelayMs = INITIAL_RECONNECT_DELAY_MS;
  }

  private openConnection(): void {
    const token = this.getToken?.();
    if (!token) {
      return;
    }

    this.closeEventSource();

    const url = `${axios.defaults.baseURL}/sse?token=${encodeTokenForSse(token)}`;
    this.eventSource = new EventSource(url);

    this.eventSource.onopen = () => {
      this.reconnectDelayMs = INITIAL_RECONNECT_DELAY_MS;
    };

    for (const [event, handlers] of this.listeners.entries()) {
      this.eventSource.addEventListener(event, (messageEvent: MessageEvent) => {
        let data: unknown;
        try {
          data = JSON.parse(messageEvent.data);
        } catch {
          return;
        }

        handlers.forEach((handler) => handler(data));
      });
    }

    this.eventSource.onerror = () => {
      this.closeEventSource();
      if (!this.intentionalClose) {
        this.scheduleReconnect();
      }
    };
  }

  private scheduleReconnect(): void {
    this.clearReconnectTimeout();
    this.reconnectTimeout = setTimeout(() => {
      this.openConnection();
    }, this.reconnectDelayMs);
    this.reconnectDelayMs = Math.min(this.reconnectDelayMs * 2, MAX_RECONNECT_DELAY_MS);
  }

  private clearReconnectTimeout(): void {
    if (this.reconnectTimeout !== null) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  private closeEventSource(): void {
    this.eventSource?.close();
    this.eventSource = null;
  }
}

export const frontendSseClient = new FrontendSseClient();
